/**
 * Pure helpers for the waitlist v2 referral payloads. Kept apart from `waitlistApi.ts`
 * so components can use them even when tests mock the API module.
 */

export interface WaitlistStats {
  total: number;
  foundingLimit: number;
  foundingSpotsLeft: number;
}

export interface WaitlistReferralLookup {
  referralCode: string;
  position: number;
  referrals: number;
  founding: boolean;
}

/** A person's place in line, normalised from either the POST or the ref lookup response. */
export interface WaitlistStanding {
  referralCode: string;
  /** 1 = first in line. Null when the server did not send a usable number. */
  position: number | null;
  referrals: number;
  /** Null when the server did not say. */
  founding: boolean | null;
}

export const REFERRAL_CODE_PATTERN = /^[A-Z0-9]{4,16}$/;

/** Uppercases and validates a referral code. Returns null for anything the server would reject. */
export function normalizeReferralCode(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const code = value.trim().toUpperCase();
  return REFERRAL_CODE_PATTERN.test(code) ? code : null;
}

function positiveInt(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 1
    ? Math.floor(value)
    : null;
}

/** Reads v2 referral fields. Returns null for older responses without a referral code. */
export function readWaitlistStanding(body: unknown): WaitlistStanding | null {
  if (!body || typeof body !== 'object') return null;
  const record = body as Record<string, unknown>;
  const referralCode = normalizeReferralCode(record.referralCode);
  if (!referralCode) return null;
  const referrals =
    typeof record.referrals === 'number' && Number.isFinite(record.referrals) && record.referrals > 0
      ? Math.floor(record.referrals)
      : 0;
  return {
    referralCode,
    position: positiveInt(record.position),
    referrals,
    founding: typeof record.founding === 'boolean' ? record.founding : null,
  };
}

/** Reads the stats payload; null when it is not usable (never invent a number). */
export function readWaitlistStats(body: unknown): WaitlistStats | null {
  if (!body || typeof body !== 'object') return null;
  const record = body as Record<string, unknown>;
  const spots = record.foundingSpotsLeft;
  if (typeof spots !== 'number' || !Number.isFinite(spots) || spots < 0) return null;
  const limit =
    typeof record.foundingLimit === 'number' && Number.isFinite(record.foundingLimit) && record.foundingLimit > 0
      ? Math.floor(record.foundingLimit)
      : 100;
  const total = typeof record.total === 'number' && Number.isFinite(record.total) ? record.total : 0;
  return { total, foundingLimit: limit, foundingSpotsLeft: Math.min(Math.floor(spots), limit) };
}
